import { useMemo, useState } from 'react';
import { ArrowRight, MessagesSquare } from 'lucide-react';
import Container from '../ui/Container';
import SectionHeading from '../ui/SectionHeading';
import Reveal from '../ui/Reveal';
import Accordion from '../ui/Accordion';
import Button from '../ui/Button';
import { faqCategories, faqs } from '../../data/faqs';
import cn from '../../lib/cn';

/**
 * FAQ block. `limit` shows a short preview (Home); the full version adds
 * category filter tabs.
 */
export default function FaqSection({ limit, showFilters = true, className }) {
  const [category, setCategory] = useState('All');

  const visible = useMemo(() => {
    const filtered = category === 'All' ? faqs : faqs.filter((f) => f.category === category);
    return limit ? filtered.slice(0, limit) : filtered;
  }, [category, limit]);

  return (
    <section className={cn('section bg-surface', className)} id="faq">
      <Container>
        <SectionHeading
          eyebrow="Questions answered"
          title="Frequently asked questions"
          description="Pickup logistics, accepted materials, compliance, EPR credits, certificates and pricing — the questions procurement and compliance teams ask us most."
        />

        {showFilters && !limit && (
          <Reveal className="mt-10 flex flex-wrap justify-center gap-2">
            {['All', ...faqCategories].map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCategory(item)}
                aria-pressed={category === item}
                className={cn(
                  'rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-200',
                  category === item
                    ? 'border-brand-600 bg-brand-600 text-white shadow-soft'
                    : 'border-ink-200 bg-white text-ink-600 hover:border-brand-400 hover:text-brand-700'
                )}
              >
                {item}
              </button>
            ))}
          </Reveal>
        )}

        <Reveal className="mx-auto mt-10 max-w-3xl">
          <Accordion items={visible} defaultOpenId={visible[0]?.id} />
        </Reveal>

        <Reveal className="mx-auto mt-10 flex max-w-3xl flex-col items-center gap-4 rounded-xl2 border border-brand-200 bg-brand-50/60 p-7 text-center sm:flex-row sm:text-left">
          <span className="grid h-12 w-12 flex-none place-items-center rounded-xl bg-white text-brand-700 shadow-soft">
            <MessagesSquare className="h-6 w-6" />
          </span>
          <div className="flex-1">
            <p className="font-display text-base font-semibold text-ink-900">
              Still have a question?
            </p>
            <p className="mt-1 text-sm text-ink-600">
              Our compliance desk responds within four working hours.
            </p>
          </div>
          <div className="flex flex-none gap-3">
            {limit && (
              <Button to="/process#faq" variant="outline">
                All FAQs
              </Button>
            )}
            <Button to="/contact">
              Contact us
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
